import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Stack,
  Avatar,
  Collapse,
  useMediaQuery,
  useTheme,
  Pagination,
  PaginationItem,
  Fade,
  Grow,
  Skeleton,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  LocalShipping as VehicleIcon,
  CalendarToday as CalendarIcon,
  Phone as PhoneIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { saveAs } from "file-saver";

// Constants
// Constants
const PAGE_SIZE = 50; // was 10
const PAGE_SIZE_OPTIONS = [50, 100, 150,200];

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[8],
  },
  position: "relative",
  overflow: "visible",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: "4px 4px 0 0",
  },
}));

const FarmerCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
  backgroundColor: theme.palette.grey[50],
  borderLeft: `4px solid ${theme.palette.success.main}`,
  borderRadius: theme.spacing(1),
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
    transform: "translateX(4px)",
    boxShadow: theme.shadows[2],
  },
}));

const FilePreviewContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[900],
  borderRadius: theme.spacing(1),
  overflow: "hidden",
  position: "relative",
  minHeight: 400,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const SearchContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  boxShadow: theme.shadows[2],
  position: "sticky",
  top: 0,
  zIndex: 10,
  backdropFilter: "blur(10px)",
  backgroundColor: "rgba(255, 255, 255, 0.95)",
}));

const HeaderPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: "#FFD700",
  color: "#000000",
  borderRadius: theme.spacing(2),
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    top: -50,
    right: -50,
    width: 200,
    height: 200,
    borderRadius: "50%",
    background: "rgba(0, 0, 0, 0.06)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: -80,
    left: -80,
    width: 300,
    height: 300,
    borderRadius: "50%",
    background: "rgba(0, 0, 0, 0.04)",
  },
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: "center",
  background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.grey[50]})`,
  borderRadius: theme.spacing(2),
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[4],
  },
}));

// Memoized Farmer Item Component
const FarmerItem = React.memo(
  ({ farmer, index, onOpenFile, onDownloadFile }) => {
    const getFileType = useCallback((url) => {
      if (!url) return "unknown";
      const ext = url.split(".").pop().split("?")[0].toLowerCase();
      if (["pdf"].includes(ext)) return "pdf";
      if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
      return "unknown";
    }, []);

    const renderFilePreview = (fileUrl) => {
      const type = getFileType(fileUrl);
      if (type === "pdf") {
        return (
          <Box
            sx={{
              p: 2,
              bgcolor: "error.light",
              borderRadius: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              minHeight: 150,
              justifyContent: "center",
            }}
          >
            <PdfIcon sx={{ fontSize: 48, color: "error.main" }} />
            <Typography variant="body2" color="error.main" fontWeight="bold">
              PDF Document
            </Typography>
            <Typography variant="caption" color="error.dark">
              Tap to view
            </Typography>
          </Box>
        );
      }
      return (
        <Box
          component="img"
          src={fileUrl}
          alt="Bill Document"
          sx={{
            width: "100%",
            height: 200,
            objectFit: "cover",
            borderRadius: 1,
            bgcolor: "grey.200",
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src =
              "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZTBlMGUwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJzYW5zLXNlcmlmIiBmb250LXNpemU9IjE4IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pjwvc3ZnPg==";
          }}
        />
      );
    };

    return (
      <Grow in timeout={300 + index * 100}>
        <FarmerCard elevation={1}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Chip
              label={`#${index + 1}`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Typography
              variant="body2"
              color="textSecondary"
              fontFamily="monospace"
            >
              {farmer.farmerSaralId}
            </Typography>
          </Box>

          <Stack spacing={1}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="textSecondary" fontWeight="bold">
                System:
              </Typography>
              <Typography variant="body2">{farmer.systemName}</Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="textSecondary" fontWeight="bold">
                Pump:
              </Typography>
              <Typography variant="body2">
                {farmer.pumpData?.name || "N/A"}
              </Typography>
            </Box>

            {farmer.billPhoto && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  fontWeight="bold"
                  gutterBottom
                >
                  Bill Document ({getFileType(farmer.billPhoto).toUpperCase()}):
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {renderFilePreview(farmer.billPhoto)}
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button
                      variant="contained"
                      color="info"
                      size="small"
                      startIcon={<ViewIcon />}
                      fullWidth
                      onClick={() => onOpenFile(farmer.billPhoto)}
                    >
                      View
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<DownloadIcon />}
                      fullWidth
                      onClick={() => onDownloadFile(farmer.billPhoto)}
                    >
                      Download
                    </Button>
                  </Stack>
                </Box>
              </Box>
            )}
          </Stack>
        </FarmerCard>
      </Grow>
    );
  },
);

// Memoized Dispatch Item Component
const DispatchItem = React.memo(({ item, onOpenFile, onDownloadFile }) => {
  const [expanded, setExpanded] = useState(true);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Fade in timeout={500}>
      <StyledCard>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
              <VehicleIcon />
            </Avatar>
          }
          subheader={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <CalendarIcon fontSize="small" color="action" />
              <Typography variant="body2" color="textSecondary">
                {formatDate(item.dispatchDate)}
              </Typography>
            </Box>
          }
          action={
            <Tooltip title={expanded ? "Collapse" : "Expand"}>
              <IconButton
                onClick={() => setExpanded(!expanded)}
                sx={{
                  backgroundColor: expanded ? "primary.light" : "grey.100",
                  "&:hover": {
                    backgroundColor: expanded ? "primary.main" : "grey.300",
                  },
                }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          }
        />

        <Collapse in={expanded}>
          <CardContent>
            {/* Driver Information */}
            <Paper
              sx={{
                p: 2.5,
                bgcolor: "grey.50",
                mb: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Driver Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Name:</strong> {item.driverName}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Contact:</strong> {item.driverContact}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <VehicleIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Vehicle:</strong> {item.vehicleNumber}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Dispatch Date:</strong>{" "}
                      {formatDate(item.dispatchDate)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Farmers Section */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Farmers ({item.farmers.length})
              </Typography>
              {item.farmers.length === 0 ? (
                <Typography
                  color="textSecondary"
                  fontStyle="italic"
                  sx={{ p: 3, textAlign: "center" }}
                >
                  No farmers in this dispatch
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {item.farmers.map((farmer, idx) => (
                    <FarmerItem
                      key={idx}
                      farmer={farmer}
                      index={idx}
                      onOpenFile={onOpenFile}
                      onDownloadFile={onDownloadFile}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </CardContent>
        </Collapse>
      </StyledCard>
    </Fade>
  );
});

// Main Component
const NewInstallationHistory = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Form handling with react-hook-form
const { register, watch, setValue } = useForm({
  defaultValues: {
    searchQuery: "",
    pageSize: 50, // was PAGE_SIZE (now equivalent, but explicit)
  },
});

  const searchQuery = watch("searchQuery");
  const pageSize = watch("pageSize");

  // State management
  const [allData, setAllData] = useState([]);
  const [displayedData, setDisplayedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);

  // File viewer states
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileLoading, setFileLoading] = useState(true);
  const [fileError, setFileError] = useState(false);
  const [fileType, setFileType] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Tracks the most recent fetch request so stale/out-of-order
  // responses (e.g. page 1 resolving after page 2) get ignored.
  const requestIdRef = React.useRef(0);

  // Fetch data with pagination
  const fetchDispatchHistory = useCallback(
    async (pageNum = 1, searchQueryParam = "", size = PAGE_SIZE) => {
      const currentRequestId = ++requestIdRef.current;

      try {
        setLoading(true);
        setError(null);

        // Build query parameters
        const params = {
          pageNo: pageNum,
          limit: size,
        };

        // Add search query if present
        if (searchQueryParam.trim()) {
          params.search = searchQueryParam.trim();
        }

        console.log(`Fetching page ${pageNum} with limit ${size}`, params);

        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL_API_URL}/admin/get-all-dispatch-history`,
          {
            params: params,
            headers: {
              ServiceId: "679b10c19cffe98b71683bc5",
            },
          },
        );

        // ✅ If a newer request has started since this one was fired,
        // discard this response — it's stale and would overwrite
        // the correct page's data.
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        console.log("API Response:", response.data);

        if (response.data.success) {
          const data = response.data.data || [];

          // ✅ FIX: API returns `totalRecord` and `totalPages`, not `total`/`count`.
          // Previously the code looked for fields that don't exist, so it fell
          // back to `data.length` (just the current page size) which broke
          // Next/Prev after the first page.
          const total =
            response.data.totalRecord ?? response.data.total ?? data.length;
          const pages =
            response.data.totalPages ?? Math.ceil(total / size) ?? 1;

          setTotalCount(total);
          setTotalPages(pages);
          setAllData(data);
          setDisplayedData(data);
          setPage(pageNum);
        } else {
          throw new Error(response?.data?.message || "Failed to fetch data");
        }
      } catch (err) {
        if (currentRequestId !== requestIdRef.current) {
          // Stale error from an aborted/outdated request — ignore it too.
          return;
        }
        console.error("Error fetching dispatch history:", err);
        setError(err.message || "Failed to load dispatch history");
        setSnackbar({
          open: true,
          message: err.message || "Failed to load data",
          severity: "error",
        });
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [],
  );

  // Initial fetch
  useEffect(() => {
    fetchDispatchHistory(1, "", pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  // Handle search (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        fetchDispatchHistory(1, searchQuery, pageSize);
      } else {
        fetchDispatchHistory(1, "", pageSize);
      }
    }, 500); // Debounce search

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Handle page change
  const handlePageChange = (event, value) => {
    if (value < 1 || value > totalPages) return;
    if (searchQuery.trim()) {
      fetchDispatchHistory(value, searchQuery, pageSize);
    } else {
      fetchDispatchHistory(value, "", pageSize);
    }
    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setValue("pageSize", newSize);
    setPage(1);
    if (searchQuery.trim()) {
      fetchDispatchHistory(1, searchQuery, newSize);
    } else {
      fetchDispatchHistory(1, "", newSize);
    }
  };

  // File handling functions
  const getFileType = useCallback((url) => {
    if (!url) return "unknown";
    const ext = url.split(".").pop().split("?")[0].toLowerCase();
    if (["pdf"].includes(ext)) return "pdf";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
    return "unknown";
  }, []);

  const downloadFile = useCallback(async (fileUrl) => {
    try {
      setDownloading(true);
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const extension = fileUrl.split(".").pop().split("?")[0].toLowerCase();
      const filename = `bill_document_${Date.now()}.${extension}`;

      saveAs(blob, filename);

      setSnackbar({
        open: true,
        message: "File downloaded successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Download error:", err);
      setSnackbar({
        open: true,
        message: "Failed to download file. Please try again.",
        severity: "error",
      });
    } finally {
      setDownloading(false);
    }
  }, []);

  const openFile = useCallback(
    (fileUrl) => {
      const type = getFileType(fileUrl);
      setSelectedFile(fileUrl);
      setFileType(type);
      setFileModalOpen(true);
      setFileLoading(true);
      setFileError(false);
    },
    [getFileType],
  );

  const closeFileModal = useCallback(() => {
    setFileModalOpen(false);
    setSelectedFile(null);
    setFileType(null);
    setFileLoading(false);
    setFileError(false);
  }, []);

  const clearSearch = useCallback(() => {
    setValue("searchQuery", "");
  }, [setValue]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    clearSearch();
    setPage(1);
    fetchDispatchHistory(1, "", pageSize);
  }, [clearSearch, fetchDispatchHistory, pageSize]);

  // Render loading skeletons
  const renderSkeletons = () => (
    <Stack spacing={2}>
      {[1, 2, 3].map((i) => (
        <Paper key={i} sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={60} />
          <Skeleton variant="text" sx={{ mt: 1 }} />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="rectangular" height={100} sx={{ mt: 1 }} />
        </Paper>
      ))}
    </Stack>
  );

  // Render search bar
  const renderSearchBar = useCallback(
    () => (
      <SearchContainer elevation={2}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          <TextField
            fullWidth
            placeholder="Search by Driver, Vehicle, Farmer ID, System or Pump..."
            variant="outlined"
            size="medium"
            {...register("searchQuery")}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "white",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <Tooltip title="Clear search">
                    <IconButton size="small" onClick={clearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              minWidth: { xs: "100%", sm: "200px" },
            }}
          >
            <TextField
              select
              size="small"
              value={pageSize}
              onChange={handlePageSizeChange}
              sx={{
                minWidth: 100,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "white",
                },
              }}
              SelectProps={{
                native: true,
              }}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option} / page
                </option>
              ))}
            </TextField>

            <Tooltip title="Refresh">
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  backgroundColor: "white",
                  "&:hover": { backgroundColor: "grey.100" },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {searchQuery && (
          <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label={`${displayedData.length} results found`}
              color="primary"
              size="small"
              icon={<FilterIcon />}
            />
            <Typography variant="caption" color="textSecondary">
              of {totalCount} total dispatches
            </Typography>
          </Box>
        )}
      </SearchContainer>
    ),
    [
      searchQuery,
      pageSize,
      displayedData.length,
      totalCount,
      clearSearch,
      handleRefresh,
      refreshing,
      register,
      handlePageSizeChange,
    ],
  );

  // Render pagination controls
  const renderPagination = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        mt: 3,
        pt: 2,
        borderTop: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <Typography variant="body2" color="textSecondary">
        Showing {displayedData.length} of {totalCount} dispatches
        {searchQuery && ` (filtered)`}
        {` - Page ${page} of ${totalPages}`}
      </Typography>

      <Pagination
        count={totalPages}
        page={page}
        onChange={handlePageChange}
        color="primary"
        size={isMobile ? "small" : "medium"}
        showFirstButton={!isMobile}
        showLastButton={!isMobile}
        siblingCount={isMobile ? 0 : 1}
        boundaryCount={isMobile ? 1 : 2}
        renderItem={(item) => (
          <PaginationItem
            {...item}
            components={{
              first: FirstPageIcon,
              last: LastPageIcon,
              previous: PrevIcon,
              next: NextIcon,
            }}
          />
        )}
        sx={{
          "& .MuiPaginationItem-root": {
            borderRadius: 2,
            fontWeight: 500,
            "&.Mui-selected": {
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: "white",
              "&:hover": {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              },
            },
          },
        }}
      />

      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PrevIcon />}
          onClick={() => handlePageChange(null, page - 1)}
          disabled={page <= 1}
          sx={{ borderRadius: 2 }}
        >
          {!isMobile && "Previous"}
        </Button>
        <Button
          variant="outlined"
          size="small"
          endIcon={<NextIcon />}
          onClick={() => handlePageChange(null, page + 1)}
          disabled={page >= totalPages}
          sx={{ borderRadius: 2 }}
        >
          {!isMobile && "Next"}
        </Button>
      </Box>
    </Box>
  );

  // Render empty state
  const renderEmpty = useCallback(
    () => (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Box sx={{ mb: 3 }}>
          <ErrorIcon sx={{ fontSize: 80, color: "grey.400" }} />
        </Box>
        <Typography variant="h6" color="textSecondary" gutterBottom>
          {searchQuery
            ? "No results found for your search"
            : "No dispatch history found"}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          {searchQuery
            ? "Try adjusting your search terms or clear the search"
            : "New dispatches will appear here once created"}
        </Typography>
        <Box>
          {searchQuery ? (
            <Button
              variant="contained"
              onClick={clearSearch}
              startIcon={<ClearIcon />}
              sx={{ borderRadius: 2 }}
            >
              Clear Search
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{ borderRadius: 2 }}
            >
              Refresh
            </Button>
          )}
        </Box>
      </Box>
    ),
    [searchQuery, clearSearch, handleRefresh],
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: "text.secondary" }}>
            Loading dispatch history...
          </Typography>
        </Box>
        {renderSkeletons()}
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <HeaderPaper elevation={4}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, color: "#000000" }}>
              📋 Installation History
            </Typography>
            <Typography variant="body1" sx={{ color: "#000000", opacity: 0.75 }}>
              Track and manage all dispatch records
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <StatsCard elevation={0}>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {totalCount}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Total Dispatches
                  </Typography>
                </StatsCard>
              </Grid>
              <Grid item xs={6}>
                <StatsCard elevation={0}>
                  <Typography variant="h4" fontWeight="bold" color="secondary">
                    {totalPages}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Total Pages
                  </Typography>
                </StatsCard>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </HeaderPaper>

      {/* Search Bar */}
      {renderSearchBar()}

      {/* Error Display */}
      {error && (
        <Fade in>
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setError(null)}
            icon={<ErrorIcon />}
          >
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        </Fade>
      )}

      {/* Top Previous / Next Bar */}
      {displayedData.length > 0 && totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "background.paper",
            boxShadow: 1,
          }}
        >
          <Button
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            startIcon={<PrevIcon />}
            onClick={() => handlePageChange(null, page - 1)}
            disabled={page <= 1}
            sx={{ borderRadius: 2 }}
          >
            Previous
          </Button>

          <Typography variant="body2" color="textSecondary" fontWeight="bold">
            Page {page} of {totalPages}
          </Typography>

          <Button
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            endIcon={<NextIcon />}
            onClick={() => handlePageChange(null, page + 1)}
            disabled={page >= totalPages}
            sx={{ borderRadius: 2 }}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Main List */}
      {displayedData.length === 0 ? (
        renderEmpty()
      ) : (
        <>
          <Stack spacing={2}>
            {displayedData.map((item) => (
              <DispatchItem
                key={item._id}
                item={item}
                onOpenFile={openFile}
                onDownloadFile={downloadFile}
              />
            ))}
          </Stack>

          {/* Pagination */}
          {totalPages > 1 && renderPagination()}
        </>
      )}

      {/* File Viewer Modal */}
      <Dialog
        open={fileModalOpen}
        onClose={closeFileModal}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {fileType === "pdf" ? "📄 PDF Viewer" : "🖼️ Image Viewer"}
          </Typography>
          <IconButton onClick={closeFileModal} size="small" sx={{ borderRadius: 2 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ p: 0, bgcolor: "grey.900", position: "relative" }}
        >
          <FilePreviewContainer>
            {fileLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(0,0,0,0.85)",
                }}
              >
                <CircularProgress size={50} color="primary" />
                <Typography sx={{ mt: 2, color: "white" }}>
                  {fileType === "pdf" ? "Loading PDF..." : "Loading Image..."}
                </Typography>
              </Box>
            )}

            {fileError && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(0,0,0,0.9)",
                  p: 3,
                }}
              >
                <ErrorIcon sx={{ fontSize: 60, color: "error.main" }} />
                <Typography variant="h6" color="error.main" sx={{ mt: 2 }}>
                  Failed to load {fileType}
                </Typography>
                <Typography color="grey.400" sx={{ mt: 1, mb: 3 }}>
                  You can download or open the file directly
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={() => downloadFile(selectedFile)}
                    sx={{ borderRadius: 2 }}
                  >
                    Download
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => window.open(selectedFile, "_blank")}
                    sx={{ borderRadius: 2 }}
                  >
                    Open in Browser
                  </Button>
                </Stack>
              </Box>
            )}

            {selectedFile &&
              !fileError &&
              (fileType === "pdf" ? (
                <iframe
                  src={`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(selectedFile)}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    minHeight: 500,
                  }}
                  title="PDF Viewer"
                  onLoad={() => setFileLoading(false)}
                  onError={() => {
                    setFileLoading(false);
                    setFileError(true);
                  }}
                />
              ) : (
                <Box
                  component="img"
                  src={selectedFile}
                  alt="Document"
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                  onLoad={() => setFileLoading(false)}
                  onError={() => {
                    setFileLoading(false);
                    setFileError(true);
                  }}
                />
              ))}
          </FilePreviewContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid", borderColor: "grey.200" }}>
          <Button
            variant="contained"
            color="success"
            startIcon={
              downloading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DownloadIcon />
              )
            }
            onClick={() => downloadFile(selectedFile)}
            disabled={downloading}
            fullWidth
            sx={{ borderRadius: 2 }}
          >
            {downloading ? "Downloading..." : `Download ${fileType?.toUpperCase()}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
          iconMapping={{
            success: <Box>✅</Box>,
            error: <Box>❌</Box>,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default NewInstallationHistory;